import React, {useEffect} from "react"
import {useAccessToken} from "../../services/auth"
import {useNavigate} from "react-router-dom"
import {Authorization, release} from "../../resources/authorization"
import {isOrg} from "../../services/orgAuth"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {useModal} from "react-modal-hook"
import {ConfirmationModal} from "../ConfirmationModal/ConfirmationModal"
import {useToasts} from "react-toast-notifications"
import numeral from "numeral"
import {DataTable, DataTableBody, DataTableCell, DataTableHead, DataTableRow} from "../DataTable/DataTable"
import PagingNavBar from "../PagingNavBar/PagingNavBar"
import {
	AccountAuthorizationsColumns,
	AllowedAccountAuthorizationsColumns,
	buildAuthorizationRowCells,
} from "./accountAuthorizationsTableCells"

function AuthorizationRow({
	au,
	refreshPage,
	includedColumns,
}: {
	au: Authorization
	refreshPage: () => void
	includedColumns: Array<AccountAuthorizationsColumns>
}) {
	const navigate = useNavigate()
	const isOrgAuthorization = isOrg()
	const [releaseState, releaseAction] = useAsyncResultIdle(release)
	const accessToken = useAccessToken()
	const {addToast} = useToasts()

	const [showReleaseConfirmationModal, hideReleaseConfirmationModal] = useModal(
		() => (
			<ConfirmationModal
				title="Release Authorization"
				close={hideReleaseConfirmationModal}
				okButtonClassname={"button is-success is-outlined"}
				cancelButtonClassname="button is-white"
				onSubmit={() => {
					releaseAction(accessToken, au.id)
				}}
			>
				You chose to cancel an authorization for <b>{numeral(au.attributes.amount / 100).format("$0,0.00")}</b>, on the
				card ending in <b>{au.attributes.cardLast4Digits}</b>. This will release the hold on the account, Are you sure
				you want to release the authorization?
			</ConfirmationModal>
		),
		[]
	)

	useEffect(() => {
		releaseState.match(
			() => null,
			() => null,
			() => refreshPage(),
			(e) => addToast(e.errors[0].title, {appearance: "error"})
		)
	}, [releaseState])

	const content = buildAuthorizationRowCells(au, includedColumns, {
		onReleaseAuthorization: () => showReleaseConfirmationModal(),
	})

	return (
		<DataTableRow
			onClick={(e) => {
				e.preventDefault()
				navigate(`/${isOrgAuthorization ? "org-" : ""}authorization/${au.id}`)
			}}
		>
			{content}
		</DataTableRow>
	)
}

export function AuthorizationsTable({
	authorizations,
	hasResults,
	hasPrev,
	hasNext,
	prev,
	next,
	isUsingPaging,
	refreshPage,
	includedColumns,
	sortFunction,
	sortBy,
}: {
	authorizations: Array<Authorization>
	hasResults: boolean
	hasPrev: boolean
	hasNext: boolean
	prev: () => void
	next: () => void
	isUsingPaging: boolean
	refreshPage: () => void
	includedColumns: Array<AllowedAccountAuthorizationsColumns>
	sortFunction: () => void
	sortBy: string
}) {
	return (
		<div className={"authorizations-table"}>
			<DataTable
				isEmpty={authorizations.length === 0}
				fullHeight={false}
				stickyAction={false}
				noContentText={"No authorizations found"}
			>
				<DataTableHead>
					<DataTableRow>
						{Object.entries(includedColumns).map((column) => {
							if (column[1] === AccountAuthorizationsColumns.createdAt) {
								return (
									<DataTableCell
										clickable
										onClick={() => sortFunction()}
										key={column[1]}
										sortable
										sortApplied={sortBy === "createdAt"}
									>
										{column[1]}
									</DataTableCell>
								)
							}
							return <DataTableCell key={column[0]}>{column[1]}</DataTableCell>
						})}
					</DataTableRow>
				</DataTableHead>
				<DataTableBody>
					{authorizations.map((au) => (
						<AuthorizationRow
							au={au}
							includedColumns={includedColumns}
							key={`${au.relationships.account?.data.id}_${au.id}`}
							refreshPage={refreshPage}
						/>
					))}
				</DataTableBody>
			</DataTable>
			<PagingNavBar
				hasResults={hasResults}
				hasPrev={hasPrev}
				hasNext={hasNext}
				prev={prev}
				next={next}
				isShow={isUsingPaging}
			/>
		</div>
	)
}
